import React, { Fragment, useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Box,
  Typography,
  Stack,
  Theme,
  useMediaQuery,
  Button,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField as MTextField } from '@mui/material';
import {
  RecordTimeline,
  Timeline,
  useRecordEvents,
} from '@react-admin/ra-audit-log';
import EditIcon from '@mui/icons-material/Edit';

import {
  AutocompleteArrayInput,
  AutocompleteInput,
  DateInput,
  DateTimeInput,
  Edit,
  Labeled,
  NullableBooleanInput,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceField,
  ReferenceInput,
  required,
  SelectArrayInput,
  TextInput,
  useGetManyReference,
  useRecordContext,
  useTranslate,
  TextField,
  FunctionField,
  InputHelperText,
  SelectInput,
  useInput,
  TabbedForm,
  FormTab,
  Toolbar,
  FormDataConsumer,
  choices,
  SelectField,
  SimpleForm,
  EditContextProvider,
  useGetOne,
  RichTextField,
  useNotify,
  SaveButton,
  useRefresh,
  useUpdate,
  useRedirect,
} from 'react-admin';

// import { useController, useFormContext } from 'react-hook-form';
import { Client, Funeral } from '../types';
import FuneralAside from './FuneralAside';
import { ClientInputWithCreate } from '../components/Clients/ClientInputWithCreate';
import ClientPreviewButton from '../components/Clients/ClientPreviewButton';
import { useWatch } from 'react-hook-form';
import {
  ClearButtons,
  FormatButtons,
  LevelSelect,
  LinkButtons,
  ListButtons,
  QuoteButtons,
  RichTextInput,
  RichTextInputProps,
  RichTextInputToolbar,
} from 'ra-input-rich-text';
import { ReferenceManyToManyInput } from '@react-admin/ra-relationships';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation } from 'react-router-dom';
import FuneralPlaceOfDeathInput from './FuneralPlaceOfDeathInput';
import FuneralPickupAddressInput from './FuneralPickupAddressInput';
import { FuneralCars } from './FuneralCars';
import { FuneralDateOfDeathFields } from './FuneralDateOfDeathFields';
import FuneralDateTimeOfFuneralInput from './FuneralDateTimeOfFuneralInput';
import { FuneralSaveButton } from './FuneralSaveButton';
import {
  EditInDialogButton,
  FormDialogContext,
} from '@react-admin/ra-form-layout';
import ClientEditBase from '../clients/ClientEditBase';
import { getToken } from '../authentication/authProvider';
import AuditTimeline from './AuditTimeline';
import { ClientSelectList } from '../payments/ClientSelectList';
import { DeceasedClientImageUpload } from './DeceasedClientImageUpload';
import { FuneralClientsInput } from './FuneralClientsInput';

export const FuneralBaseEdit = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  const isXSmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  const cemeteryOptionsRenderer = (choice: any) => {
    return (
      <Box>
        {choice.name}
        <br />
        {choice.address}
      </Box>
    );
  };

      return (
      <>
      {record?.archived_at && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This funeral is archived
        </Alert>
      )}
      <TabbedForm
        warnWhenUnsavedChanges
        toolbar={
        <Toolbar>
          <FuneralSaveButton />
        </Toolbar>
      }
    >
      <TabbedForm.Tab label="Deceased">
        <Grid container width={{ xs: '100%', xl: 800 }} spacing={1}>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Region</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <RadioButtonGroupInput
              source="region"
              choices={[
                { id: 'quebec', name: 'Quebec' },
                { id: 'ontario', name: 'Ontario' }
              ]}
              defaultValue="quebec"
              validate={required()}
              helperText={false}
            />
            <NeedTypeInput />
          </Grid>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Client Information</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <Box display={{ xs: 'block', sm: 'flex' }}>
              <Box flex={1} mr={{ xs: 0, sm: '0.5em' }} mb={1}>
                <ClientInputWithCreate source="deceased_client_id" />
              </Box>
              <Box
                flex={1}
                mr={{ xs: 0, sm: '0.5em' }}
                sx={{ display: 'flex' }}
              >
                {/* <ClientPreview /> */}
              </Box>
            </Box>
            <Box mb={2} mt={1}>
              <SelectInput
                source="language"
                label="Language"
                choices={[
                  { id: 'en', name: 'English' },
                  { id: 'fr', name: 'French' }
                ]}
                defaultValue="en"
                fullWidth
              />
            </Box>
            <Typography variant="subtitle1" gutterBottom>Funeral Clients</Typography>
            <FuneralClientsInput />
          </Grid>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Death Information</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <FuneralDateOfDeathFields />

           
            <div>
              <FuneralPlaceOfDeathInput />
              <FuneralPickupAddressInput />
            </div>
            <div>
              <ReferenceInput
                reference="cause_of_deaths"
                source="cause_of_death_id"
                sort={{ field: 'id', order: 'ASC' }}
              >
                <SelectInput optionText="name" helperText={false} />
              </ReferenceInput>
            </div>
            <div>
              <TextInput source="doctor_name" helperText={false} />
            </div>
            <div>
              <TextInput source="sp3_number" helperText={false} />
            </div>
            <div>
              <TextInput source="benefits_confirmation_number" label="Benefits Confirmation Number" helperText={false} />
            </div>
            {/* Only once the column exists — see BenefitsStatusField. */}
            {record && 'benefits_ineligible_reason' in record && (
              <div>
                <TextInput source="benefits_ineligible_reason" label="Benefits Ineligible Reason" helperText={false} />
              </div>
            )}
            <div>
              <TextInput source="death_certificate_number" label="Death Certificate Number" helperText={false} />
            </div>
            <div>
              {/* Test: Replace BooleanInput with working alternative */}
              <NullableBooleanInput
                source="public_curator"
                label="Public Curator"
                helperText={false}
              />
            </div>

            <div>
              <NullableBooleanInput
                source="coroner"
                label="Coroner"
                helperText={false}
              />
            </div>

            <div>
              <InfectiousDiseaseInput />
            </div>

            <div>
              <NumberInput
                source="number_death_certificates"
                helperText={false}
              />
            </div>
            <div>
              <DateInput
                source="death_certificate_date"
                label="Death Certificate Issue Date"
                helperText={false}
              />
            </div>
            <div>
              <SelectInput
                source="firearms_permit"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'no', name: 'No' },
                  { id: 'yes', name: 'Yes' },
                  { id: 'unknown', name: "I don't know" }
                ]}
                defaultValue="no"
              />
            </div>

            <div>
              <SelectInput
                source="contractor_license"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'no', name: 'No' },
                  { id: 'yes', name: 'Yes' },
                  { id: 'unknown', name: "I don't know" }
                ]}
                defaultValue="no"
              />
            </div>

            <FormDataConsumer>
              {({ formData }) =>
                formData.contractor_license === 'yes' && (
                  <div>
                    <TextInput
                      source="contractor_license_number"
                      label="RBQ licence number"
                      fullWidth
                      helperText={false}
                      parse={(v: string) => (v === '' ? null : v)}
                    />
                  </div>
                )
              }
            </FormDataConsumer>

            <div>
              <SelectInput
                source="public_sector_pension"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'no', name: 'No' },
                  { id: 'yes', name: 'Yes' },
                  { id: 'unknown', name: "I don't know" }
                ]}
                defaultValue="no"
              />
            </div>

            <div>
              <SelectInput
                source="disability_benefits"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'no', name: 'No' },
                  { id: 'yes', name: 'Yes' },
                  { id: 'unknown', name: "I don't know" }
                ]}
                defaultValue="no"
              />
            </div>
            <div>
              <TextInput
                multiline
                fullWidth
                source="special_instructions"
                helperText={false}
              />
            </div>
            <div>
              {/* Same column as the note on the delivery card in the show
                  sidebar, and the same 200-char CHECK constraint guards it —
                  cap it here too or a long note fails at the database. */}
              <TextInput
                multiline
                fullWidth
                source="crematorium_special_instructions"
                label="Crematorium Special Instructions"
                helperText="Shown to the crematorium on their schedule page. Max 200 characters."
                inputProps={{ maxLength: 200 }}
              />
            </div>

            <Typography variant="subtitle1" mt={2} gutterBottom>Social Security Benefits</Typography>
            <div>
              <SelectInput
                source="benefits_program"
                label="Benefits Program"
                fullWidth
                choices={[
                  { id: 'cpp', name: 'CPP (Service Canada)' },
                  { id: 'qpp', name: 'QPP (Retraite Québec)' }
                ]}
                emptyText="Default (CPP for Ontario, QPP for Quebec)"
                format={(v) => v ?? ''}
                parse={(v) => (v === '' ? null : v)}
                helperText="Override only for exception cases"
              />
            </div>
            <div>
              <NullableBooleanInput
                source="foreign_social_security"
                label="Contributed to foreign Social Security"
                helperText={false}
              />
            </div>
            <div>
              <FormDataConsumer>
                {({ formData }) =>
                  formData.foreign_social_security && (
                    <TextInput
                      source="foreign_social_security_country"
                      label="Foreign Social Security Country"
                      fullWidth
                      helperText={false}
                    />
                  )
                }
              </FormDataConsumer>
            </div>
            <div>
              <NullableBooleanInput
                source="children_after_1958"
                label="Had children born after 1958"
                helperText={false}
                fullWidth
              />
            </div>
            <div>
              <NullableBooleanInput
                source="children_provided_after_1958"
                label="Adopted or provided for children born after 1958"
                helperText={false}
                fullWidth
              />
            </div>
            <div>
              <NullableBooleanInput
                source="children_under_18_at_death"
                label="Children under 18 at death"
                helperText={false}
                fullWidth
              />
            </div>
            <div>
              <SelectInput
                source="quebec_pension"
                label="Government Pension Plan"
                fullWidth
                helperText="Was your loved one receiving pension benefits from, or making contributions to, a government pension plan?"
                choices={[
                  { id: 'yes-qpp', name: 'Yes, Quebec Pension Plan (QPP)' },
                  { id: 'yes-cpp', name: 'Yes, Canadian Pension Plan (CPP)' },
                  { id: 'no', name: 'No' }
                ]}
              />
            </div>
            <div>
              <SelectInput
                source="capacity_for_death_benefit"
                label="Capacity for applying for the death benefit"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'person_who_paid', name: 'Person who paid' },
                  { id: 'heir', name: 'Heir' },
                  { id: 'liquidator_of_estate', name: 'Liquidator of estate' },
                  { id: 'professional_mandates', name: 'Professional mandates to settle the estate' },
                  { id: 'charitable_organization', name: 'Charitable organization paying' }
                ]}
              />
            </div>
            <FormDataConsumer>
              {({ formData }) =>
                formData.capacity_for_death_benefit === 'person_who_paid' && (
                  <>
                    <div>
                      <NullableBooleanInput
                        source="is_heir"
                        label="Are they an Heir?"
                        helperText={false}
                      />
                    </div>
                    <div>
                      <NullableBooleanInput
                        source="has_rescinded_claim"
                        label="Has the Heir Rescinded the Claim?"
                        helperText={false}
                      />
                    </div>
                  </>
                )
              }
            </FormDataConsumer>
            <div>
              <SelectInput
                source="urn_type"
                label="Urn Type"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'cardboard', name: 'Cardboard' },
                  { id: 'custom', name: 'Custom' },
                ]}
              />
            </div>
            <div>
              <SelectInput
                source="split_ashes"
                label="Split Ashes"
                fullWidth
                helperText={false}
                choices={[
                  { id: 'no-split', name: 'Do Not Split' },
                  { id: 'split-two', name: 'Split in 2 Equal Parcels' },
                  { id: 'split-three', name: 'Split in 3 Equal Parcels' },
                  { id: 'split-other', name: 'Other Split Configuration' },
                ]}
              />
            </div>
            
          </Grid>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Admin Information</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <ReferenceInput
              source="director_id"
              reference="profiles"
              filter={{ role: 'director', active: 'true' }}
            >
              <AutocompleteInput
                optionText="name"
                filterToQuery={(searchText) => ({
                  'name@ilike': searchText,
                  active: true,
                })}
                helperText={false}
              />
            </ReferenceInput>
            <OwnerField />
            <TextInput
              source="internal_notes"
              fullWidth
              multiline
              helperText="This section is private and DOES NOT appear on the daily schedule"
            />
          </Grid>
        </Grid>
      </TabbedForm.Tab>

      <FormTab label="Website">
        <Grid container width={{ xs: '100%', xl: 800 }} spacing={1}>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Website</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            {/* <BooleanInput source="web_publish" helperText="This must be checked to create the funeral in the paperman/admin. To make it visible to the public select Live below." label="Send to Website" /> */}
            <NullableBooleanInput
              source="is_live"
              helperText={false}
              label="Live on Website"
            />
            <NullableBooleanInput
              source="web_sticky"
              helperText={false}
              label="Sticky on Website"
            />
            <NullableBooleanInput
              source="web_show_dates"
              defaultValue={true}
              helperText={false}
              label="Show Dates"
            />
            <TextInput
              source="web_name"
              helperText={false}
              label="Web Name"
              fullWidth
            />
            <Labeled label="Funeral Slug (Auto-generated)">
              <FunctionField
                source="slug"
                render={(record: Funeral) => {
                  if (!record?.slug) return null;
                  const isLocal =
                    window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1';
                  const baseUrl = isLocal
                    ? 'http://localhost:3010'
                    : 'https://cleocremation.com';
                  return (
                    <a
                      href={`${baseUrl}/obituaries/${record.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 14, fontFamily: 'monospace' }}
                    >
                      {record.slug}
                    </a>
                  );
                }}
              />
            </Labeled>

       
            <div>
              <FormDataConsumer>
                {({ formData, ...rest }) =>
                  formData.web_funeral_select == 'other' && (
                    <>
                      <TextInput
                        source="web_funeral_note"
                        {...rest}
                        helperText={false}
                        label="Funeral Custom Note"
                        fullWidth
                      />
                    </>
                  )
                }
              </FormDataConsumer>
            </div>

          
        
            <CustomRichTextField
              source="web_notes"
              label="Website Notes"
            />

            <DeceasedClientImageUpload />
          </Grid>
        </Grid>
      </FormTab>

      
      <FormTab label="Obituary">
        <Grid container width={{ xs: '100%', xl: 800 }} spacing={1}>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Obituary</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <div>
              <CustomRichTextField
                source="obituary_en"
                label="English Obituary"
              />{' '}
            </div>
          </Grid>
        </Grid>
      </FormTab>
     
      <FormTab label="Aftercare">
        <Grid container width={{ xs: '100%', xl: 800 }} spacing={1}>
          {isXSmall ? null : (
            <Grid item xs={4}>
              <Box mt={1}>Aftercare</Box>
            </Grid>
          )}
          <Grid item xs={8}>
            <div>
              <NullableBooleanInput
                source="aftercare"
                label="Family Requests Aftercare"
                helperText={false}
              />
            </div>
            <div>
              {/* <ClientSelectList funeral_id={record.id} required={false} source="aftercare_contact" /> */}
            </div>
            <div>
            <SelectInput
                  source="aftercare_status"
                  helperText={false}
                  choices={[
                    { id: 'message', name: 'Left Message' },
                    { id: 'waiting', name: 'Waiting for Information' },
                    { id: 'ready', name: 'Ready to Submit' },
                    { id: 'not-submitting', name: 'Not Submitting' },
                    { id: 'submitted', name: 'Submitted' },



                  ]}
                />
            </div>
            <div>
              <RichTextInput
                source="aftercare_notes"
                fullWidth
                helperText={false}
              />
            </div>
            {record && <ArchiveSection />}
          </Grid>
        </Grid>
      </FormTab>
      {/* <FormTab label="Log">
      <Grid container width={{ xs: '100%', xl: 800 }} spacing={1}>
          {isXSmall ? null :
            <Grid item xs={4}>
              <Box mt={1}>Log</Box>
            </Grid>
          }
          <Grid item xs={8}>
            <AuditTimeline />
          </Grid>
        </Grid>
      </FormTab> */}
    </TabbedForm>
    </>
  );
};

const CustomRichTextField = (props: RichTextInputProps) => (
  <RichTextInput
    fullWidth
    toolbar={
      <RichTextInputToolbar>
        <LevelSelect size="small" />
        <FormatButtons size="small" />
        <ListButtons size="small" />
        <LinkButtons size="small" />
        <QuoteButtons size="small" />
        <ClearButtons size="small" />
      </RichTextInputToolbar>
    }
    {...props}
  />
);

// Create-only: drives the deal_type of the quote auto-created with the funeral.
// `_deal_type` is a form-only field consumed by the data provider, not a funerals column.
const NeedTypeInput = () => {
  const location = useLocation();

  if (!location.pathname.includes('/funerals/create')) {
    return null;
  }

  return (
    <RadioButtonGroupInput
      source="_deal_type"
      label="Type"
      choices={[
        { id: 'at_need', name: 'At-Need' },
        { id: 'pre_need', name: 'Pre-Need' },
      ]}
      defaultValue="at_need"
      helperText={false}
    />
  );
};

const OwnerField = () => {
  const location = useLocation();

  if (!location.pathname.includes('/funerals/create')) {
    return (
      <ReferenceInput
        source="owner_id"
        reference="profiles"
        filter={{ active: 'true', role: 'coordinator' }}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(searchText) => ({
            'name@ilike': searchText,
            active: true,
          })}
          helperText={false}
        />
      </ReferenceInput>
    );
  } else {
    return null;
  }
};


const ClientPreview = () => {
  const record = useRecordContext<Funeral>();

  // const clientID = useWatch<Funeral>({ name: `deceased_client_id` });
  const { data: client } = useGetOne<Client>('clients', {
    id: record?.deceased_client_id,
  });

  return client ? (
    <>
      <ReferenceField source="deceased_client_id" reference="clients">
        <EditInDialogButton fullWidth maxWidth="md">
          <SimpleForm>
            <ClientEditBase />
          </SimpleForm>
        </EditInDialogButton>
      </ReferenceField>
    </>
  ) : null;

  // return clientID ? <ClientPreviewButton id={clientID} /> : null;
};

const ArchiveSection = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const redirect = useRedirect();
  const [update] = useUpdate();
  const [open, setOpen] = useState(false);
  const isArchived = !!record?.archived_at;

  const handleConfirm = () => {
    void update(
      'funerals',
      {
        id: record.id,
        data: { archived_at: isArchived ? null : new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(isArchived ? 'Funeral unarchived' : 'Funeral archived', { type: 'success' });
          setOpen(false);
          if (!isArchived) {
            redirect('list', 'funerals');
          }
        },
        onError: (error: any) => {
          notify(error?.message || 'Error updating funeral', { type: 'error' });
        },
      }
    );
  };

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Archive
      </Typography>
      <Button
        variant="outlined"
        color={isArchived ? 'primary' : 'error'}
        onClick={() => setOpen(true)}
      >
        {isArchived ? 'Unarchive Funeral' : 'Archive Funeral'}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{isArchived ? 'Unarchive Funeral' : 'Archive Funeral'}</DialogTitle>
        <DialogContent>
          <Typography>
            {isArchived
              ? 'Are you sure you want to unarchive this funeral? It will reappear in the funerals list.'
              : 'Are you sure you want to archive this funeral? It will be hidden from the funerals list.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={isArchived ? 'primary' : 'error'}
            onClick={handleConfirm}
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const InfectiousDiseaseInput = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const { data: client, isLoading } = useGetOne<Client>(
    'clients',
    { id: record?.deceased_client_id },
    { enabled: !!record?.deceased_client_id }
  );

  if (!record?.deceased_client_id || isLoading) return null;

  const currentValue = client?.internal_infectious_disease;
  const selectValue = currentValue === true ? 'true' : currentValue === false ? 'false' : '';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const boolVal = val === 'true' ? true : val === 'false' ? false : null;

    void update(
      'clients',
      {
        id: record.deceased_client_id,
        data: { internal_infectious_disease: boolVal },
        previousData: client,
      },
      {
        onSuccess: () => {
          notify('Infectious disease status updated', { type: 'success' });
          refresh();
        },
        onError: (error: any) => {
          notify(error?.message || 'Error updating infectious disease status', { type: 'error' });
        },
      }
    );
  };

  return (
    <MTextField
      select
      label="Infectious Disease"
      value={selectValue}
      onChange={handleChange}
      helperText="Auto-detected from SP3. Override manually if needed."
      fullWidth
      SelectProps={{ native: true }}
      size="small"
      sx={{ mb: 1 }}
    >
      <option value="">Unknown</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </MTextField>
  );
};

const WebsitePushButton = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();

  const handleClick = async () => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}web/funerals/${record.id}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (result.status >= 200 && result.status < 300) {
        notify('Website Updated', {
          type: 'success',
        });
      } else {
        notify(`Error saving funeral to website`, {
          type: 'warning',
          autoHideDuration: 20000,
          multiLine: true,
        });
      }
    } catch (e) {
      console.warn(e);
      notify(JSON.stringify(e));
    }
  };

  return (
    <Button
      onClick={handleClick}
      // startIcon={<AddIcon />}
      // aria-label={translate('resources.creditmemos.add')}
    >
      Push to Website
    </Button>
  );
};
